import { useEffect, useRef } from "react";

export interface InboxShortcutHandlers {
  next: () => void;
  previous: () => void;
  reply: () => void;
  resolve: () => void;
}

const KEYS: Readonly<Record<string, keyof InboxShortcutHandlers>> = { j: "next", k: "previous", r: "reply", e: "resolve" };

/** True when the key press belongs to a text field, menu or dialog rather than the page. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return true;
  return target.closest("[role=dialog],[role=menu],[role=listbox]") !== null;
}

/** Shortcut for a plain key press, or null (modifiers, typing, repeats). */
export function shortcutFor(event: Pick<KeyboardEvent, "key" | "altKey" | "ctrlKey" | "metaKey" | "repeat" | "target">): keyof InboxShortcutHandlers | null {
  if (event.altKey || event.ctrlKey || event.metaKey || event.repeat) return null;
  if (isTypingTarget(event.target)) return null;
  return KEYS[event.key.toLowerCase()] ?? null;
}

/** Inbox keyboard navigation: j/k next/previous, r reply, e resolve. */
export function useInboxShortcuts(handlers: InboxShortcutHandlers): void {
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  }, [handlers]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const action = shortcutFor(event);
      if (!action) return;
      event.preventDefault();
      ref.current[action]();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);
}
